import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { AlertTriangle, MapPinOff, Clock, Loader2 } from "lucide-react";

const fmt = (s) => (s ? new Date(s).toLocaleString() : "—");

function ManualCountyInline({ order, dataset, onSet }) {
  const [stateSel, setStateSel] = useState("");
  const [fips, setFips] = useState("");
  const [busy, setBusy] = useState(false);

  const states = useMemo(() => Array.from(new Set((dataset || []).map((c) => c.state))).sort(), [dataset]);
  const counties = useMemo(() => (dataset || []).filter((c) => c.state === stateSel), [dataset, stateSel]);

  const save = async () => {
    if (!fips) return;
    setBusy(true);
    try {
      await api.post(`/admin/orders/${order.id}/service-county`, { county_fips: fips });
      toast.success("County set — routing can proceed");
      onSet();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not set county");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-2 flex flex-wrap items-end gap-2" data-testid={`manual-county-picker-${order.id}`}>
      <div>
        <Label className="text-[11px]">State</Label>
        <select data-testid={`manual-county-state-${order.id}`} value={stateSel} onChange={(e) => { setStateSel(e.target.value); setFips(""); }} className="mt-1 flex h-9 w-28 rounded-md border border-input bg-background px-2 text-sm">
          <option value="">State…</option>
          {states.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <div>
        <Label className="text-[11px]">County</Label>
        <select data-testid={`manual-county-fips-${order.id}`} value={fips} onChange={(e) => setFips(e.target.value)} disabled={!stateSel} className="mt-1 flex h-9 w-52 rounded-md border border-input bg-background px-2 text-sm">
          <option value="">Select county…</option>
          {counties.map((c) => <option key={c.fips} value={c.fips}>{c.name}</option>)}
        </select>
      </div>
      <Button size="sm" data-testid={`manual-county-save-${order.id}`} disabled={busy || !fips} onClick={save} className="bg-slate-900 text-white hover:bg-slate-800">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Set county"}
      </Button>
    </div>
  );
}

export const EscalatedNotaryOrders = () => {
  const [escalated, setEscalated] = useState([]);
  const [manual, setManual] = useState([]);
  const [dataset, setDataset] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const load = () => Promise.all([
    api.get("/admin/notary/escalated-orders").then(({ data }) => setEscalated(data)).catch(() => setEscalated([])),
    api.get("/admin/notary/manual-county-orders").then(({ data }) => setManual(data)).catch(() => setManual([])),
  ]).finally(() => setLoaded(true));

  useEffect(() => {
    load();
    import("@/data/usCounties.json").then((m) => setDataset((m.default || m).counties || [])).catch(() => {});
  }, []);

  if (!loaded) return null;
  if (escalated.length === 0 && manual.length === 0) return null;

  return (
    <div className="mb-6 space-y-4" data-testid="notary-routing-admin">
      {manual.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="manual-county-orders">
          <h3 className="flex items-center gap-2 font-display text-base font-bold text-amber-900">
            <MapPinOff className="h-4 w-4" /> Notary orders needing a county ({manual.length})
          </h3>
          <p className="text-xs text-amber-800">These addresses couldn't be geocoded to a county. Pick the correct county so routing and ranking can proceed.</p>
          <div className="mt-3 space-y-3">
            {manual.map((o) => (
              <div key={o.id} data-testid={`manual-county-order-${o.id}`} className="rounded-lg border border-amber-200 bg-white p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-slate-900">{o.job_number || "Notary job"}</span>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">{o.service_type || "Notary"}</span>
                  <span className="text-xs text-slate-500">{o.service_address || "—"}</span>
                </div>
                {dataset ? <ManualCountyInline order={o} dataset={dataset} onSet={load} />
                  : <p className="mt-1 text-xs text-slate-400">Loading county list…</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {escalated.length > 0 && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4" data-testid="escalated-orders">
          <h3 className="flex items-center gap-2 font-display text-base font-bold text-red-900">
            <AlertTriangle className="h-4 w-4" /> Escalated notary orders ({escalated.length})
          </h3>
          <p className="text-xs text-red-800">No provider claimed these within the primary and widened windows. They now need admin action (assign or follow up).</p>
          <div className="mt-3 space-y-2">
            {escalated.map((o) => (
              <div key={o.id} data-testid={`escalated-order-${o.id}`} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-red-200 bg-white p-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-slate-900">{o.job_number || "Notary job"}</span>
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">{o.service_type || "Notary"}</span>
                    {o.needs_manual_county && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700">Needs county</span>}
                  </div>
                  <div className="mt-1 flex flex-wrap gap-3 text-xs text-slate-400">
                    <span>{o.service_area || o.service_county_name || "—"}</span>
                    <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> escalated {fmt(o.routing_stage_at)}</span>
                    {o.deadline && <span>deadline {fmt(o.deadline)}</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
